import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Shield,
  Palette,
  Lock,
  Eye,
  EyeOff,
  AlertTriangle,
  Trash2,
  Settings,
} from 'lucide-react';
import { Card, Button, Input, Tabs, Modal } from '@/components/ui';
import { useAuthStore, useThemeStore } from '@/stores';
import { userApi, authApi } from '@/services/api';
import { useNotification } from '@/hooks';
import { useValidationRules } from '@/contexts';

export const SettingsManager: React.FC = () => {
  const navigate = useNavigate();
  const notification = useNotification();
  const { user, logout } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const validationRules = useValidationRules();
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);

  const [profileForm, setProfileForm] = useState({
    username: user?.username || '',
    email: user?.email || '',
  });

  useEffect(() => {
    if (user) {
      setProfileForm((prev) => ({
        username: prev.username || user.username || '',
        email: prev.email || user.email || '',
      }));
    }
  }, [user?.username, user?.email]);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  const [deleteAccountModal, setDeleteAccountModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');

  const maskEmail = (email: string): string => {
    if (!email) return '';
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 2) {
      return `${localPart[0]}***@${domain}`;
    }
    const masked = localPart[0] + '*'.repeat(localPart.length - 2) + localPart[localPart.length - 1];
    return `${masked}@${domain}`;
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await userApi.updateSettings({
        profile: {
          username: profileForm.username,
        }
      });
      notification.auth.profileUpdateSuccess();
    } catch (error: any) {
      notification.auth.profileUpdateFailed(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      notification.error('密码不匹配', '两次输入的密码不一致');
      return;
    }

    if (passwordForm.newPassword.length < validationRules.PASSWORD_MIN_LENGTH) {
      notification.error('密码太短', `密码至少需要${validationRules.PASSWORD_MIN_LENGTH}个字符`);
      return;
    }
    if (passwordForm.newPassword.length > validationRules.PASSWORD_MAX_LENGTH) {
      notification.error('密码太长', `密码最多${validationRules.PASSWORD_MAX_LENGTH}个字符`);
      return;
    }

    setIsLoading(true);
    try {
      await authApi.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      notification.auth.passwordChangeSuccess();
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
    } catch (error: any) {
      notification.auth.passwordChangeFailed(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== '删除我的账户') {
      notification.error('请输入正确的确认文字');
      return;
    }

    if (!deletePassword) {
      notification.error('请输入密码');
      return;
    }

    setIsLoading(true);
    try {
      const response = await authApi.deleteAccount({
        password: deletePassword,
        confirmText: deleteConfirmText,
      });

      if (response.success) {
        notification.auth.accountDeleted();
        setDeleteAccountModal(false);
        setTimeout(() => {
          logout();
          navigate('/');
        }, 2000);
      } else {
        notification.error('删除失败', response.message || '操作失败');
      }
    } catch (error: any) {
      notification.error('删除失败', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderDeleteAccountModal = () => (
    <Modal
      isOpen={deleteAccountModal}
      onClose={() => {
        setDeleteAccountModal(false);
        setDeleteConfirmText('');
        setDeletePassword('');
      }}
      title="删除账户"
      size="md"
    >
      <div className="space-y-4">
        <div className="p-4 bg-gradient-to-r from-error-50 to-error-100/50 dark:from-error-900/20 dark:to-error-800/20 rounded-xl border border-error-200 dark:border-error-800">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-error-500 to-error-600 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-error-700 dark:text-error-400 mb-1">
                警告：此操作无法撤销
              </h4>
              <p className="text-sm text-error-600 dark:text-error-400">
                删除账户将永久移除您的所有数据，包括：
              </p>
              <ul className="text-sm text-error-600 dark:text-error-400 mt-2 list-disc list-inside">
                <li>个人资料和设置</li>
                <li>所有收藏夹</li>
                <li>搜索历史记录</li>
                <li>社区分享的搜索源</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="p-3 bg-surface-50 dark:bg-surface-800/50 rounded-xl">
          <p className="text-sm text-surface-600 dark:text-surface-400 mb-2">
            请输入以下文字确认删除：
          </p>
          <p className="font-mono font-bold text-surface-900 dark:text-surface-100">
            删除我的账户
          </p>
        </div>

        <Input
          type="text"
          placeholder="请输入确认文字"
          value={deleteConfirmText}
          onChange={(e) => setDeleteConfirmText(e.target.value)}
          fullWidth
        />

        <Input
          label="密码"
          type="password"
          placeholder="请输入当前密码以确认身份"
          value={deletePassword}
          onChange={(e) => setDeletePassword(e.target.value)}
          leftIcon={<Lock className="w-5 h-5" />}
          fullWidth
        />

        <div className="flex gap-3">
          <Button
            variant="outline"
            fullWidth
            onClick={() => setDeleteAccountModal(false)}
          >
            取消
          </Button>
          <Button
            variant="danger"
            fullWidth
            onClick={handleDeleteAccount}
            isLoading={isLoading}
          >
            确认删除账户
          </Button>
        </div>
      </div>
    </Modal>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center shadow-lg shadow-primary-500/25">
          <Settings className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-surface-900 dark:text-surface-100">
            系统设置
          </h2>
          <p className="text-surface-500 dark:text-surface-400">
            管理您的账户和偏好设置
          </p>
        </div>
      </div>

      <Tabs
        tabs={[
          { id: 'profile', label: '个人资料', icon: <User className="w-4 h-4" /> },
          { id: 'security', label: '安全设置', icon: <Shield className="w-4 h-4" /> },
          { id: 'appearance', label: '外观设置', icon: <Palette className="w-4 h-4" /> },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      {activeTab === 'profile' && (
        <Card className="p-6 border-surface-200/50 dark:border-surface-700/50 shadow-lg">
          <h3 className="text-lg font-semibold text-surface-900 dark:text-surface-100 mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <User className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            </div>
            个人资料
          </h3>
          <form onSubmit={handleUpdateProfile} className="space-y-4 max-w-md">
            <Input
              label="用户名"
              value={profileForm.username}
              onChange={(e) => setProfileForm({ ...profileForm, username: e.target.value })}
              fullWidth
            />
            <div className="space-y-2">
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300">
                邮箱
              </label>
              <div className="p-3 bg-surface-50 dark:bg-surface-800/50 rounded-xl">
                <span className="text-surface-900 dark:text-surface-100">
                  {maskEmail(user?.email || '')}
                </span>
              </div>
            </div>
            <Button type="submit" variant="primary" isLoading={isLoading}>
              保存更改
            </Button>
          </form>
        </Card>
      )}

      {activeTab === 'security' && (
        <div className="space-y-6">
          <Card className="p-6 border-surface-200/50 dark:border-surface-700/50 shadow-lg">
            <h3 className="text-lg font-semibold text-surface-900 dark:text-surface-100 mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-success-100 dark:bg-success-900/30 flex items-center justify-center">
                <Lock className="w-4 h-4 text-success-600 dark:text-success-400" />
              </div>
              修改密码
            </h3>
            <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
              <Input
                label="当前密码"
                type={showPassword ? 'text' : 'password'}
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                leftIcon={<Lock className="w-5 h-5" />}
                fullWidth
              />
              <Input
                label="新密码"
                type={showPassword ? 'text' : 'password'}
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                leftIcon={<Lock className="w-5 h-5" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-surface-400 hover:text-surface-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                }
                fullWidth
              />
              <Input
                label="确认新密码"
                type={showPassword ? 'text' : 'password'}
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                leftIcon={<Lock className="w-5 h-5" />}
                fullWidth
              />
              <Button type="submit" variant="primary" isLoading={isLoading}>
                修改密码
              </Button>
            </form>
          </Card>

          <Card className="p-6 border-error-200 dark:border-error-800 shadow-lg bg-gradient-to-r from-error-50/50 to-error-100/30 dark:from-error-900/10 dark:to-error-800/10">
            <h3 className="text-lg font-semibold text-error-600 dark:text-error-400 mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-error-100 dark:bg-error-900/30 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-error-600 dark:text-error-400" />
              </div>
              危险操作
            </h3>
            <p className="text-sm text-surface-600 dark:text-surface-400 mb-4">
              以下操作不可撤销，请谨慎操作
            </p>
            <Button
              variant="danger"
              onClick={() => setDeleteAccountModal(true)}
              leftIcon={<Trash2 className="w-4 h-4" />}
            >
              删除账户
            </Button>
          </Card>
        </div>
      )}

      {activeTab === 'appearance' && (
        <Card className="p-6 border-surface-200/50 dark:border-surface-700/50 shadow-lg">
          <h3 className="text-lg font-semibold text-surface-900 dark:text-surface-100 mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center">
              <Palette className="w-4 h-4 text-accent-600 dark:text-accent-400" />
            </div>
            外观设置
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">
                主题模式
              </label>
              <div className="flex gap-3">
                {[
                  { value: 'light', label: '浅色', icon: '☀️' },
                  { value: 'dark', label: '深色', icon: '🌙' },
                  { value: 'system', label: '跟随系统', icon: '💻' },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setTheme(option.value as 'light' | 'dark' | 'system')}
                    className={`flex items-center gap-2 px-5 py-3 rounded-xl border-2 transition-all duration-200 ${
                      theme === option.value
                        ? 'border-primary-500 bg-gradient-to-r from-primary-50 to-accent-50 text-primary-600 dark:from-primary-900/20 dark:to-accent-900/20 dark:text-primary-400 shadow-md'
                        : 'border-surface-200 dark:border-surface-700 hover:border-primary-300 dark:hover:border-primary-600'
                    }`}
                  >
                    <span className="text-xl">{option.icon}</span>
                    <span className="font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {renderDeleteAccountModal()}
    </div>
  );
};
